import { useDeps } from '~/compositions/dependency-container'
import { useRequestError } from '~/compositions/request-error'
import { useI18n } from '~/compositions/i18n'
import RecaptchaService from '~/services/recaptcha/RecaptchaService'
import RequestBuilder from '~/builders/http/RequestBuilder'
import {
  ComponentInternalInstance,
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref
} from '@nuxtjs/composition-api'
import { FormFieldErrors, Schema } from '~/models/form/types'
import { Telephone } from '~/models/shared/types'
import {
  FallbackWidgetRenderResult,
  RecaptchaFormToken
} from '~/models/recaptcha/types'
import { toSnakeCase } from '~/utils/snake-case'
import { toCamelCase } from '~/utils/object'

interface ContactForm {
  senderId?: number
  dealers: any[]
  recipientId?: number
  recipients: any[]
  name: string
  email: string
  telephone: Telephone
  body: string
  recaptchaToken: RecaptchaFormToken
}

export function useContactForm({
  formUrl,
  recaptchaToken = 'contact_form',
  extraParams = {}
}: {
  formUrl: string
  recaptchaToken?: string
  extraParams?: any
}) {
  const contactFormComponent: ComponentInternalInstance | null = getCurrentInstance()

  const [recaptchaService, requestBuilder] = useDeps(
    RecaptchaService,
    RequestBuilder
  )
  const handleRequestError = useRequestError()
  const { t } = useI18n()

  const isReady = ref(false)
  const isLoading = ref(false)
  const isSuccess = ref(false)
  const canTradeInMessage = ref(true)
  const hasMessagesForClassified = ref(false)
  const messageStats = ref<{
    replyTimeMedian: number
  } | null>(null)
  const values = ref<ContactForm>({} as ContactForm)
  const schema = ref<Schema>({})
  const errors = ref<FormFieldErrors>({})
  const recaptchaFallbackWidgetLoaded = ref(false)
  const threadToGo = ref(null)

  const isManager = computed(
    () => !!(schema.value?.senderId && values.value.dealers.length > 2)
  )
  const managerOptions = computed(() => {
    if (!isManager.value) {
      return []
    }
    return values.value.dealers.map((dealer, index) => ({
      label: index === 0 ? `${dealer.name} (${t('manager')})` : dealer.name,
      value: dealer.id
    }))
  })
  const hasRecipients = computed(
    () => !!(schema.value?.recipientId && values.value.recipients?.length)
  )
  const recipientsOptions = computed(() => {
    if (!hasRecipients.value) {
      return []
    }
    return values.value.recipients.map(recipient => ({
      value: recipient.id,
      label: recipient.name
    }))
  })
  const recaptchaWidgetContainerId = computed(() => {
    return recaptchaService.defaultFallbackWidgetContainerId
  })

  onMounted(async () => {
    await loadV3Token()
    await getSchema()
  })

  function senderChange(id: number) {
    const userToChange =
      id === null
        ? values.value.dealers[0]
        : values.value.dealers.find(dealer => dealer.id === id)
    values.value = {
      ...values.value,
      name: userToChange.name,
      email: userToChange.email,
      telephone: userToChange.telephone
    }
  }
  async function loadV3Token() {
    let token = ''
    try {
      token = await recaptchaService.getToken(recaptchaToken)
    } catch (error) {
      handleRequestError(error)
      return
    }
    values.value = {
      ...values.value,
      recaptchaToken: {
        v: 3,
        token
      } as RecaptchaFormToken
    }
  }
  async function submitContactForm() {
    if (contactFormComponent) {
      contactFormComponent.emit('submit-contact-form')
    }
    isLoading.value = true
    if (!recaptchaFallbackWidgetLoaded.value) {
      await loadV3Token()
    }
    try {
      const data = await requestBuilder
        .request('POST', formUrl)
        .data(toSnakeCase({ ...values.value, ...extraParams }))
        .send()
      errors.value = {}
      if (data.values?.thread) {
        threadToGo.value = data.values.thread
      }
      isSuccess.value = true
    } catch (error) {
      if ((error as any).response?.data?.data?.errors) {
        errors.value = toCamelCase((error as any).response.data.data.errors)
        checkForCaptchaError(errors.value)
      }
      handleRequestError(error)
      isLoading.value = false
    }
  }
  async function checkForCaptchaError(errors: FormFieldErrors) {
    if (errors.recaptchaToken && !recaptchaFallbackWidgetLoaded.value) {
      recaptchaFallbackWidgetLoaded.value = true
      await nextTick()
      const {
        token
      }: FallbackWidgetRenderResult = await recaptchaService.renderFallbackWidget()
      values.value = {
        ...values.value,
        recaptchaToken: {
          v: 2,
          token
        } as RecaptchaFormToken
      }
    }
  }
  async function getSchema() {
    isLoading.value = true
    try {
      const data = await requestBuilder.request('GET', formUrl).send()
      canTradeInMessage.value = data.canTradeInMessage
      hasMessagesForClassified.value = data.hasMessagesForClassified
      messageStats.value = data.messageStats
      values.value = data.values || {}
      schema.value = data.schema
      isReady.value = true
    } catch (error) {
      handleRequestError(error)
    } finally {
      isLoading.value = false
    }
  }

  return {
    canTradeInMessage,
    hasMessagesForClassified,
    messageStats,
    values,
    schema,
    errors,
    isReady,
    isLoading,
    isSuccess,
    threadToGo,
    isManager,
    managerOptions,
    hasRecipients,
    recipientsOptions,
    recaptchaWidgetContainerId,
    recaptchaFallbackWidgetLoaded,
    senderChange,
    submitContactForm
  }
}
